// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
// start
import React from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { Navigation } from './components/Navigation';
import { HeroSection } from './components/HeroSection';
import { TrustedBySection } from './components/TrustedBySection';
import { HowItWorksSection } from './components/blocks/HowItWorksSection';
import { AboutUs } from './components/blocks/AboutUs';
import { WhyChooseSection } from './components/WhyChooseSection';
import { ContactSections } from './components/blocks/ContactSections';
import { FinalCTA } from './components/blocks/FinalCTA';
import { LatestBlogSection } from './components/LatestBlogSection';
import { Footer } from './components/Footer';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfUse } from './pages/TermsOfUse';
import { NotFoundPage } from './pages/404';
import PressPage from './pages/PressPage';
import { CareersPage } from './pages/CareersPage';
import { AboutPage } from './pages/AboutPage';
import { SeniorsPage } from './pages/SeniorsPage';
import { CaregiversPage } from './pages/CaregiversPage';
import { CuraScanPage } from './pages/CuraScanPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { PitchPage } from './pages/PitchPage';

import { EnterpriseApiPage } from './pages/EnterpriseApiPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { ScrollProvider } from './components/ScrollProvider';

import { PageTransition } from './components/PageTransition';
import { CookiePolicy } from './pages/CookiePolicy';
import { AcceptableUsePage } from './pages/AcceptableUsePage';
import { ScrollToTop } from './components/ScrollToTop';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

const App: React.FC = () => {


  const menuItems = [
    { label: 'Features', href: '#features' },
    { label: 'About Us', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Partnerships', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <ScrollProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <PageTransition>
                <div className="min-h-screen bg-white relative">

                  <div className="relative z-10">
                    <Helmet>
                      <title>Cura – AI Medical Assistant for Nonprofits & Community Organizations</title>
                      <meta name="description" content="Cura partners with nonprofits and community organizations to bring instant, AI-powered health answers to the seniors and caregivers they serve." />
                      <link rel="canonical" href="https://mycura.org/" />
                      <meta property="og:title" content="Cura – AI Medical Assistant for Nonprofits & Community Organizations" />
                      <meta property="og:description" content="Cura partners with nonprofits and community organizations to bring instant, AI-powered health answers to the seniors and caregivers they serve, via voice, chat, or WhatsApp." />
                      <meta property="og:url" content="https://mycura.org/" />
                      <meta property="og:type" content="website" />
                      <meta property="og:image" content="https://mycura.org/Demo2.png" />
                      <meta name="twitter:card" content="summary_large_image" />
                      <meta name="twitter:image" content="https://mycura.org/Demo2.png" />
                      <meta name="twitter:title" content="Cura – AI Medical Assistant for Nonprofits & Community Organizations" />
                      <meta name="twitter:description" content="Cura partners with nonprofits and community organizations to bring instant, AI-powered health answers to the seniors and caregivers they serve, via voice, chat, or WhatsApp." />
                      <script type="application/ld+json">
                        {JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "FAQPage",
                          "mainEntity": [
                            {
                              "@type": "Question",
                              "name": "What is Cura?",
                              "acceptedAnswer": { "@type": "Answer", "text": "Cura is an AI-powered medical assistant that provides instant health guidance via voice, chat, and WhatsApp. Nonprofits and community organizations partner with Cura to help the seniors and caregivers they serve get reliable medical answers anytime, anywhere." }
                            },
                            {
                              "@type": "Question",
                              "name": "How does Cura work?",
                              "acceptedAnswer": { "@type": "Answer", "text": "Cura uses AI trained on NIH and FDA medical datasets to assess symptoms, provide health guidance, and help users determine the right level of care, from self-treatment to urgent care." }
                            },
                            {
                              "@type": "Question",
                              "name": "How can my organization partner with Cura?",
                              "acceptedAnswer": { "@type": "Answer", "text": "Nonprofits and community organizations can partner with Cura to offer AI-powered health guidance to the people they serve. Reach out at founders@mycura.org to start the conversation. We handle setup, onboarding, and support." }
                            },
                            {
                              "@type": "Question",
                              "name": "Is health data safe with Cura?",
                              "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Cura uses enterprise-grade encryption and follows strict privacy protocols to protect the personal and medical information of everyone who uses Cura." }
                            }
                          ]
                        })}
                      </script>
                      <script type="application/ld+json">
                        {JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "BreadcrumbList",
                          "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" }
                          ]
                        })}
                      </script>
                    </Helmet>
                    <Navigation
                      menuItems={menuItems}
                      scrollToSection={scrollToSection}
                      scrollToTop={scrollToTop}
                    />
                    <main>
                      <HeroSection />
                      <TrustedBySection />
                      <WhyChooseSection />
                      <HowItWorksSection />
                      <AboutUs />
                      <LatestBlogSection />
                      <ContactSections />
                      <FinalCTA />
                    </main>
                    <Footer />
                  </div>
                </div>
              </PageTransition>
            }
          />
          <Route path="/press" element={<PressPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/enterprise-api" element={<EnterpriseApiPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/job/:id" element={<JobDetailPage />} />
          <Route path="/pitch" element={<PitchPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/seniors" element={<SeniorsPage />} />
          <Route path="/caregivers" element={<CaregiversPage />} />
          <Route path="/scan" element={<CuraScanPage />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUsePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </ScrollProvider>
  );
};

export default App;
// end
